import { CheckCircle } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  createProAlert,
  updateProAlertEmails,
} from '../../../../api/pro-alert';
import IconStatusButton from '../../../../common-components/icon-status-button/icon-status-button';
import SimpleSearchBar from '../../../../common-components/simple-search-bar/simple-search-bar';
import { useAppDispatch } from '../../../../hooks';
import useUserRoleAndShortId from '../../../../hooks/user-details/useUserDetails';
import { MyFleetVessel } from '../../../../models/my-fleet.model';
import { ProAlert } from '../../../../models/pro.model';
import {
  addProAlert,
  updateProAlert,
} from '../../../../state/pro-alerts/pro-alerts.slice';
import {
  getCountAnalyticsEventLabel,
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../../../utils/analytics';
import EmailListItem from './email-list-item';
import { emailsChanged, validateEmails } from './pro-alert-helpers';

function renderEmailInputArea({
  emailInput,
  setEmailInput,
  valid,
  invalidEmails,
  duplicateEmails,
  emails,
  editingEmailIdx,
  uniqueEmails,
  setUniqueEmails,
  submitting,
}: {
  emailInput: string;
  setEmailInput: (val: string) => void;
  valid: boolean;
  invalidEmails: string[];
  duplicateEmails: string[];
  emails: string[];
  editingEmailIdx: number | null;
  uniqueEmails: string[];
  setUniqueEmails: (val: string[]) => void;
  submitting: boolean;
}) {
  return (
    <>
      <TextField
        sx={{ flex: 1, mb: 1 }}
        value={emailInput}
        onChange={(e) => setEmailInput(e.target.value)}
        error={!valid}
        helperText={
          !valid
            ? [
                invalidEmails.length > 0 &&
                  `Invalid email(s): ${invalidEmails.join(', ')}`,
                duplicateEmails.length > 0 &&
                  `Duplicate email(s): ${duplicateEmails.join(', ')}`,
              ]
                .filter(Boolean)
                .join(' | ')
            : `*Enter emails separated by commas.`
        }
        label="Email Addresses"
        required
        fullWidth
        placeholder="recipient1@email.com, recipient2@email.com"
        type="email"
        inputProps={{
          'aria-label': 'PRO Alert Email Input',
          'data-testid': 'pro-alert-email-input',
        }}
      />
      <Button
        variant="outlined"
        onClick={() => {
          if (valid && emails.length > 0) {
            setUniqueEmails([...uniqueEmails, ...emails]);
            setEmailInput('');
          }
        }}
        disabled={
          !valid || !emailInput.trim() || editingEmailIdx !== null || submitting
        }
        sx={{ mb: 2 }}
        aria-label="Add Emails button"
        data-testid="add-pro-alert-emails-button"
      >
        Add Email(s)
      </Button>
      <Divider />
    </>
  );
}

function renderRecipientEmailsList({
  vessel,
  uniqueEmails,
  filteredEmails,
  editingEmailIdx,
  setEditingEmailIdx,
  setUniqueEmails,
  submitting,
  searchFilterValue,
  setSearchFilterValue,
}: {
  vessel: MyFleetVessel;
  uniqueEmails: string[];
  filteredEmails: string[];
  editingEmailIdx: number | null;
  setEditingEmailIdx: (idx: number | null) => void;
  setUniqueEmails: (emails: string[]) => void;
  submitting: boolean;
  searchFilterValue: string;
  setSearchFilterValue: (val: string) => void;
}) {
  return (
    <>
      {uniqueEmails.length > 0 && (
        <Box sx={{ mr: 1.5, mt: 1, ml: '-0.5rem' }}>
          <SimpleSearchBar
            handleChange={setSearchFilterValue}
            filterValue={searchFilterValue}
            placeholder="Search Recipients"
            dataTestId="pro-alert-recipients-search-input"
          />
        </Box>
      )}
      <List sx={{ maxHeight: '30vh', overflowY: 'auto', padding: 0 }}>
        {filteredEmails.length === 0 && (
          <ListItem>
            <ListItemText
              sx={{ textAlign: 'center' }}
              primary={
                uniqueEmails.length === 0
                  ? 'Minimum of one recipient email is required.'
                  : 'No matching search results.'
              }
            />
          </ListItem>
        )}
        {filteredEmails.map((email) => {
          const originalIdx = uniqueEmails.indexOf(email);
          return (
            <EmailListItem
              key={`${vessel.vessel_id}-${email}`}
              email={email}
              emails={uniqueEmails}
              editing={editingEmailIdx === originalIdx}
              onStartEdit={() => setEditingEmailIdx(originalIdx)}
              onUpdate={(newEmail) => {
                const updated = [...uniqueEmails];
                updated[originalIdx] = newEmail;
                setUniqueEmails(updated);
                setEditingEmailIdx(null);
              }}
              onCancelEdit={() => setEditingEmailIdx(null)}
              onDelete={() =>
                setUniqueEmails(
                  uniqueEmails.filter((_, i) => i !== originalIdx)
                )
              }
              disabled={submitting}
            />
          );
        })}
      </List>
    </>
  );
}

function renderActionArea({
  pendingCancel,
  operationError,
  handleCancel,
  submitting,
  proAlert,
  getSubmitButtonIcon,
  getSubmitButtonContent,
  handleSubmit,
  uniqueEmails,
  editingEmailIdx,
  success,
  hasEmailChanges,
}: {
  pendingCancel: boolean;
  operationError: string | null;
  handleCancel: () => void;
  submitting: boolean;
  proAlert?: ProAlert | null;
  getSubmitButtonIcon: () => React.ReactNode;
  getSubmitButtonContent: () => string;
  handleSubmit: () => void;
  uniqueEmails: string[];
  editingEmailIdx: number | null;
  success: 'create' | 'update' | null;
  hasEmailChanges: boolean;
}) {
  return (
    <>
      {pendingCancel && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`Selecting "Cancel" again will result in loss of changes, please
          select "${
            proAlert ? 'Update Alert' : 'Submit Alert'
          }" to save changes.`}
        </Alert>
      )}
      {operationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {operationError}
        </Alert>
      )}
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'stretch',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%',
          mt: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={submitting}
          sx={{
            flex: 1,
            minWidth: 'max(30%, 120px)',
          }}
          aria-label="Cancel editing alert"
          data-testid="cancel-pro-alert-button"
        >
          Cancel
        </Button>
        <IconStatusButton
          onClick={handleSubmit}
          statusIcon={getSubmitButtonIcon()}
          statusText={getSubmitButtonContent()}
          disabled={
            !!submitting ||
            !!success ||
            uniqueEmails.length === 0 ||
            editingEmailIdx !== null ||
            (!!proAlert && !hasEmailChanges)
          }
          sx={{
            flex: 1,
            minWidth: 'max(30%, 120px)',
          }}
          aria-label={getSubmitButtonContent()}
          dataTestId="submit-pro-alert-button"
        />
      </Stack>
    </>
  );
}

interface VesselAlertFormProps {
  vessel: MyFleetVessel;
  proAlert?: ProAlert | null;
  onCancel: () => void;
  onFinish: () => void;
}

function VesselProAlert({
  vessel,
  proAlert,
  onCancel,
  onFinish,
}: VesselAlertFormProps) {
  const dispatch = useAppDispatch();
  const [uniqueEmails, setUniqueEmails] = useState<string[]>(
    proAlert?.emails || []
  );
  const [editingEmailIdx, setEditingEmailIdx] = useState<number | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<'create' | 'update' | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const { valid, invalidEmails, duplicateEmails, emails } = validateEmails(
    emailInput,
    uniqueEmails
  );
  const [searchFilterValue, setSearchFilterValue] = useState('');
  const [pendingCancel, setPendingCancel] = useState(false);
  const userDetails = useUserRoleAndShortId();

  const handleSubmit = async () => {
    setSubmitting(true);
    setOperationError(null);

    try {
      if (proAlert) {
        if (!proAlert.alert_id) throw new Error('Missing alert_id');
        const response = await updateProAlertEmails({
          alert_id: proAlert.alert_id,
          emails: uniqueEmails,
        });
        dispatch(
          updateProAlert({
            alertId: response.alert_id,
            emails: uniqueEmails,
          })
        );
        setSuccess('update');
      } else {
        if (!vessel.vessel_id) throw new Error('Missing vessel_id');
        const response = await createProAlert({
          alerts: [
            {
              vessel_id: vessel.vessel_id,
              emails: uniqueEmails,
            },
          ],
        });
        if (response.alerts && response.alerts.length > 0) {
          dispatch(
            addProAlert({
              alert_id: response.alerts[0].alert_id,
              vessel_id: vessel.vessel_id,
              emails: uniqueEmails,
              muted: false,
              created_at: new Date().toISOString(),
              updated_at: '',
            })
          );
          logAnalyticsEvent('MyFleet', 'ProAlertsCreated', [
            getUserAnalyticsEventLabel(userDetails),
            getCountAnalyticsEventLabel(response.alerts.length),
          ]);
        }
        setSuccess('create');
      }
    } catch (error: any) {
      setOperationError(
        `Failed to ${
          proAlert ? 'update' : 'create'
        } alert. Please try again later. If this continues please contact support.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Show warning, require second click if user has unsaved changes
    if (
      uniqueEmails.length >= 1 &&
      emailsChanged(uniqueEmails, proAlert?.emails) &&
      !pendingCancel
    ) {
      setPendingCancel(true);
      return;
    }
    setPendingCancel(false);
    onCancel();
  };

  const getSubmitButtonContent = () => {
    if (submitting) return proAlert ? 'Updating...' : 'Creating...';
    if (success === 'update') return 'Updated';
    if (success === 'create') return 'Created';
    return proAlert ? 'Update Alert' : 'Create Alert';
  };

  const getSubmitButtonIcon = () => {
    if (success) return <CheckCircle />;
    if (submitting) return <CircularProgress size={12} />;
    return null;
  };

  const filteredEmails = useMemo(
    () =>
      uniqueEmails?.filter((email) =>
        email.toLowerCase().includes(searchFilterValue.toLowerCase())
      ) || [],
    [uniqueEmails, searchFilterValue]
  );

  const hasEmailChanges = useMemo(
    () => !!proAlert && emailsChanged(uniqueEmails, proAlert.emails),
    [uniqueEmails, proAlert]
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (success) {
      timeout = setTimeout(() => onFinish(), 3000);
    }
    return () => clearTimeout(timeout);
  }, [success, onFinish]);

  useEffect(() => {
    if (success || operationError) {
      setSuccess(null);
      setOperationError(null);
    }
  }, [uniqueEmails, emailInput]);

  return (
    <Stack sx={{ mt: 0 }}>
      {renderEmailInputArea({
        emailInput,
        setEmailInput,
        valid,
        invalidEmails,
        duplicateEmails,
        emails,
        editingEmailIdx,
        uniqueEmails,
        setUniqueEmails,
        submitting,
      })}
      {renderRecipientEmailsList({
        vessel,
        uniqueEmails,
        filteredEmails,
        editingEmailIdx,
        setEditingEmailIdx,
        setUniqueEmails,
        submitting,
        searchFilterValue,
        setSearchFilterValue,
      })}
      {renderActionArea({
        pendingCancel,
        operationError,
        handleCancel,
        submitting,
        proAlert,
        getSubmitButtonIcon,
        getSubmitButtonContent,
        handleSubmit,
        uniqueEmails,
        editingEmailIdx,
        success,
        hasEmailChanges,
      })}
    </Stack>
  );
}

export default VesselProAlert;

VesselProAlert.defaultProps = {
  proAlert: null,
};
